import { assign, createMachine } from 'xstate'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type NavigatorMachineContext<TSession, TNode, TContent, TAnswerContent, TSelectedAnswer> = {
  currentAnswer: TSelectedAnswer | null
  currentContent: TContent | null // current question/doc from backend
  currentIndex: number
  states: TNode[] // The list of content with states from backend
  selectedIndex: number
  loadContent: (node: TNode) => Promise<TContent>
  submitAnswer: (node: TNode, answer: TSelectedAnswer | null) => Promise<TSession>
  viewDocument: (node: TNode) => Promise<TSession>
  errorMessage: string | null
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type NavigatorMachineEvent<TSession, TNode, TContent, TAnswerContent, TSelectedAnswer> =
  | {
      type: 'INITIALISE'
      loadContent: (node: TNode) => Promise<TContent>
      submitAnswer: (node: TNode, answer: TSelectedAnswer | null) => Promise<TSession>
      viewDocument: (node: TNode) => Promise<TSession>
    }
  | { type: 'GO_TO_CONTENT'; index: number }
  | { type: 'STORE_ANSWER'; answerInput: TSelectedAnswer }
  | { type: 'RELOAD' }

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type NavigatorMachineServices<TSession, TNode, TContent, TAnswerContent, TSelectedAnswer> = {
  loadFromContent: { data: TContent } // should be TNode???
  sendAnswerRequest: { data: TSession }
}

export const getNavigationMachine = <TSession, TNode, TContent, TAnswerContent, TSelectedAnswer>(
  states: TNode[],
  startIndex = 0
) => {
  return createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDsCGA3AllVAXTA9sgLKoDGAFpsmAHSYQA2YAxAJIBybAKmwIIAZNgGUAogG0ADAF1EoAA4FYmfETkgAHogC0ARn2TaAFgBMJgOySAbGYDMRgBxHbAGhABPHfYCctb5O9dAFYTXW9HI0lzAF9otzQsHFUScioaWgAzMFxKABE8VBYIIjpqdAIAazoE7DxCFMpqOiycinzcVAQygjI6oilpAfVFZWT1LQRtKyMjWklJIKCHIP1FqyDzN09J9dpdExt1yMdl8Nj4jFrk0kb0lryCljAAJ2eCZ9p5RjwM94BbWg1JL1G5pZrZB4dLrIcq9ZIDIZIEAjFT1cY6ZZBWhWBxWaZ4iyWKxbRAmQxGIJGaaSRwmWySBz7EznEBAvoNMG0Z6QTDcsi4bgEADCRFwYGQAvc8lYiIUSlRaiREwcDmxJiCgW8DlsoXp5mJHh0kVotg1hNMDgWVnMMTirMuwKIoKaXJ5fIFwtF4sl0pY4l0siRKLGSsQltoNsZVh1NLx+qMJIQhJNVn0kRVTnWuhZbOuqRd3IgvLA-MFIol3u4UplJkDctGaNDCC1tEZunMuiM-hMTgsukTHds2PM3g2RnMlMkpu8OYd7Od6QAjgBXODJIolWiwDpiwFzvO3Ogrtf1WXI+Uh0ATXT2XSt5ZUszebw6-uGya6bW0dW9maj8IdkYs6JPO+ZLqu271LQqDILAADuLzUFALDCNwADyABKogAPp8BwwgAOqiBhZ7Bo2V6IPYVitvMlJLGYJjjkEibjrMOpkhqiw3u2VjAVcIJgUeEHJNBsEIc8SEsAA4mh2HodhQpoRw3CiMppEXuRmiINaqqOHiViSME7bjps762A4vgWam6q2JY7YznauYCYetDHpBRBbsuABGfwqPgyDIcU6TdFUe4gQenJuSJsDeb5uD+VA0KwuyCIyMMGmKhRCD6BZ344mSzijo4tmJrYLapjqKoTlqZp8Y6HIulFUExT5fmSS8bwfF8Pz-GF-FOoJrnCc1sVtQFSU9ClMjqQ2mVac2+jfiEph4iskhkt4iYrCYezOKmDIvvqWp1aBLlkF6EqiK87wsFhAhoXwuQzQqyDoh+UQ7d45ihOOVhaj2KqJto9jflS3EUvq06UidEUusUZDLn83obuk254NU+7OZy8OI96z2XvNujWDRI7mLY5NRFxzHvnoKx5ZYtH2CsSww1jcM9LjEq0FgYBwZJMlybJinKap3D45pExUrMjH2NY622AZkTU9sVIRpSBlld40b0baFzhWz6Q40jXPclAmDbi8ABqmC85JQWlDClQY-rA0uUb3qumbFvPNbtvjd0cKntNaVBhlr1NlqqplQsRma0VLGqg4dLLLoFWWoZtis672Mc8buCe+bYo+zbfMBU811dd8uC-M8AJOdn7MI3nBfe77peJQHU2DCH9YvW9RhhNisvrTSizrZ2iap9RFm2Rx+rRg5ev9Q16TLvIEB1AFbDIBAYAaH6PfnrN4dZdog60EE1hLM4hkMgPQMbRGPa2De2rPt9QRZyvdBrxvCXb7vfe-o6xHz7k2M+DITT2A7JacyDgyYP1ygEacNJrAmBfF9L+C46AEHQC8HmcFpKyXksLFSalD5kTmhMbQl9zB7DHIZG08w8QTxppVb8ZMKQK0WJYOiWDBqFmLPyAAYm8P429USoEYAAveB8QGUJPvNbQKpfALHQQsDUg5tQPwCBGP6lIp5VRZo5TGDd0iCPdKIgg4jkCSOkTvWRwD0rHzeso8wqoLBlVNEEaO+plZeEYn4HUZpGLtktMyHMBBd7wCRPXb+ziwGnxynQ1YPZoz6jJjeIGPjp6LEcAEGkjFHD8JcgwZgCSCbUKJrZIJGpNbrEVm+bY2g-wmjyeYccjEOKL3tC7b+mQIRtAKBUiWOhvCBI2sES+jh9D2CBqOE0WtOyWhCEEP6eISmcgsSWD05YxQSirNKEZVCvADzmN9eBFgDKfinNk2Yv5zID1MJfGkmzGrDTmgot66DE60QpEnMwTFSrrUWQZZ88xIjWkziYvp2Chong8jBeCiEArHMURMEIQ5Ln7EMhsF5TTEADyxR2fwphvpk3WrxGFy84VNQ8i1OKCU0VvSstiLh4QnDWBOKVLWHCDgWDpKOAyn9qX1VpR85AnlWrxUgMypsOUhxLA5RsImI4FhbRBpYL6BwbQzE-NCpeYrBrnQrJdCucqkk3l8Kk3EtkoVZPfBSb8uIew-Vss+dUIrDWnRzk3b0Fr5r0jvLRImBxKaaiBmclYKpZkdNVS+N5htc4ewIUhANEwfBzBjpS8NYREx-WMG2DpJKX6py9b0mlg13YmzAF7Iubc02hxcU2dYHjLSqrCF4m0LEsRan0ByhYL4iZAVFT6xunN84ENlU2xJhMFizDWZ+bUpowiGUnt9UG9IKQ4nCBDRNP916bygDIiYvdKk6B8UOR8nYzDwOmGVHRqp346WfO2DUVLvWw2CjCKRDA9n+pneehA9JDBfRCFqcZFk7IDkWgrekP0k4BFHPu2guD8El3TUaDd2pL5mFTugpYuIgZlU+pqG8ZUDIWS1ih7ZIixESPwFIk9mGPyrE3etccwQXzrQfvsOYYQfGhFWEO2IsQgA */
      tsTypes: {} as import('./navigator.machine.typegen').Typegen0,
      schema: {
        context: {} as NavigatorMachineContext<TSession, TNode, TContent, TAnswerContent, TSelectedAnswer>,
        events: {} as NavigatorMachineEvent<TSession, TNode, TContent, TAnswerContent, TSelectedAnswer>,
        services: {} as NavigatorMachineServices<TSession, TNode, TContent, TAnswerContent, TSelectedAnswer>,
      },
      id: 'navigationMachine',
      context: {
        currentAnswer: null,
        currentContent: null,
        currentIndex: startIndex,
        selectedIndex: startIndex,
        states,
        loadContent: (node: TNode) => Promise.resolve({} as unknown as TContent),
        submitAnswer: (node: TNode, answer: TSelectedAnswer | null) => Promise.resolve({} as TSession),
        viewDocument: (node: TNode) => Promise.resolve({} as TSession),
        errorMessage: null,
      },
      initial: 'idle',
      states: {
        idle: {
          tags: ['contentNode'],
          on: {
            INITIALISE: {
              target: 'redirectFromInitialIndex',
              actions: ['saveCallbacksToContext'],
            },
          },
        },

        fetchData: {
          tags: ['contentNode'],
          invoke: {
            src: 'loadFromContent',
            onDone: { target: 'redirectToContentType', actions: ['clearPreviousSelectedAnswer'] },
            onError: 'contentError',
          },
        },

        redirectToContentType: {
          // tags: ['contentNode'],
          entry: ['saveContentDataToContext', 'saveAnsweredContentFromStateToContext', 'clearErrorMessage'],
          always: [
            {
              target: 'document',
              cond: 'isDocument',
            },
            {
              target: 'question',
              cond: 'isQuestion',
            },
            {
              target: 'invalidContent',
            },
          ],
        },

        question: {
          tags: ['contentNode'],
          initial: 'answering',
          states: {
            answering: {
              tags: ['contentNode'],
              on: {
                STORE_ANSWER: {
                  target: 'answering',
                  internal: true,
                  actions: ['storeInputtedAnswer'],
                },

                GO_TO_CONTENT: { target: 'submitting', actions: ['storeSelectedIndex'] },
              },
            },

            submitting: {
              tags: ['contentNode', 'submittingAnswer'],
              invoke: {
                src: 'sendAnswerRequest',
                onDone: { target: 'submitted', actions: ['updateStates'] },
                onError: { target: 'answering', actions: ['setAnswerErrorMessage'] },
              },
            },

            submitted: {
              entry: ['clearErrorMessage'],
              type: 'final',
            },
          },
          onDone: {
            target: 'updatingIndex',
          },
        },

        contentError: {
          entry: ['setContentErrorMessage'],
          tags: ['contentNode', 'contentError'],
          on: {
            RELOAD: 'fetchData',
          },
        },

        document: {
          tags: ['contentNode'],

          states: {
            viewing: {
              tags: ['contentNode'],
              on: {
                GO_TO_CONTENT: {
                  target: 'registerViewing',
                  actions: 'storeSelectedIndex',
                },
              },
            },

            registerViewing: {
              tags: ['contentNode'],
              invoke: {
                src: 'sendDocumentViewRequest',
                onDone: {
                  target: 'viewed',
                  actions: 'updateStates',
                },
                onError: 'viewing',
              },
            },

            viewed: {
              type: 'final',
            },
          },

          initial: 'viewing',

          onDone: {
            target: 'updatingIndex',
          },
        },

        updatingIndex: {
          // tags: ['contentNode'],
          always: [
            {
              target: 'fetchData',
              cond: 'isValidIndex',
            },
            'overview',
          ],
          entry: 'updateCurrentIndex',
        },

        invalidContent: {},

        overview: {
          tags: ['overview'],
          on: {
            GO_TO_CONTENT: { target: 'updatingIndex', actions: ['storeSelectedIndex'] },
          },
        },

        redirectFromInitialIndex: {
          always: [
            {
              cond: 'isValidIndex',
              target: 'fetchData',
            },
            'overview',
          ],
        },
      },
    },
    {
      actions: {
        updateCurrentIndex: assign({ currentIndex: (ctx) => ctx.selectedIndex }),
        // clearOldContentData: assign((ctx) => ({ ...ctx, currentContent: null, currentAnswer: null })),
        clearPreviousSelectedAnswer: assign((ctx) => ({ ...ctx, currentAnswer: null })),
        saveContentDataToContext: assign({ currentContent: (_, evt) => evt.data as TContent }),
        storeSelectedIndex: assign({ selectedIndex: (_, evt) => evt.index }),
        storeInputtedAnswer: assign({ currentAnswer: (_, evt) => evt.answerInput }),
        clearErrorMessage: assign((ctx) => ({ ...ctx, errorMessage: null })),
        // TODO: phrase keys
        setAnswerErrorMessage: assign((ctx) => ({ ...ctx, errorMessage: 'assessment-player-answer-error' })),
        setContentErrorMessage: assign((ctx) => ({ ...ctx, errorMessage: 'assessment-player-content-error' })),
        saveCallbacksToContext: assign((ctx, evt) => ({
          ...ctx,
          loadContent: evt.loadContent,
          submitAnswer: evt.submitAnswer,
          viewDocument: evt.viewDocument,
        })),
      },
      services: {
        loadFromContent: (ctx) => ctx.loadContent(ctx.states[ctx.currentIndex]),
        sendAnswerRequest: (ctx) => ctx.submitAnswer(ctx.states[ctx.currentIndex], ctx.currentAnswer),
        sendDocumentViewRequest: (ctx) => ctx.viewDocument(ctx.states[ctx.currentIndex]),
      },
    }
  )
}
